import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import {
	PageTitle,
	RevenueSpendByAccountListTable,
	ToolPanel,
	UiPagination,
	ESortOrder,
	RevenueSpendByAccountRevenueDetailsModal,
	RevenueSpendByAccountSpendDetailsModal,
	RevenueSpendByAccountOfferSpendDetailsModal,
	getAccountIsFiredBadge,
	IconView,
	IconViewOff
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import {
	useRevenueSpendByAccountList,
	useRevenueSpendByAccountFiltersPage,
	useRevenueSpendByAccountSortPage
} from '../hooks'
import * as lib from '../lib'
import * as state from '../states'
import {
	IFetchItemsSetup,
	ERoleAccess,
	TRevenueSpendByAccount,
	TRevenueSpendByAccountRevenueCpa,
	TRevenueSpendByAccountRevenueSpend,
	TRevenueSpendByAccountRevenue,
	TRevenueSpendByAccountSpend,
	EOfferType,
	TRevenueSpendByAccountOfferSpend
} from '../types'
import { TAKE, MENU, MENU_CATEGORY } from '../constants'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'


export const RevenueSpendByAccountPage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const profile = useAtomValue(state.profileAtom)
	const $access = new lib.Access(profile ?? null)

	const isBuyer = useAtomValue(state.isBuyerAtom)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0
	const buyer = isBuyer ?? lib.buyerIdSearchParams(searchParams.get('buyer') ?? '')

	const buyerItems = useAtomValue(state.accountBuyersAtom)
	const currentBuyer = buyerItems?.find(item => item.id === buyer) ?? null
	const currentBuyerIsFired = currentBuyer?.isFired
	const currentBuyerTeamReport = currentBuyer?.role?.teamReport
	const keitaroLogin: string = currentBuyer?.extra?.keitaroLogin ?? ''
	const currentBuyerFormat = `{${currentBuyerTeamReport}} - [id: ${currentBuyer?.id}] ${currentBuyer?.auth?.login ?? currentBuyer?.email ?? '?'} (${keitaroLogin ?? ''})`
	const isRevenueSpendByAccountPageIndex = !buyer
	const pageTitle = `${t(MENU_CATEGORY.revenue.title)} ${t(MENU.revenueSpendReportByAccount.title)}`
	const pageTitleFormat = `${pageTitle}${!isRevenueSpendByAccountPageIndex ? ': ' + currentBuyerFormat : ''}`

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		revenueSpendByAccountFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useRevenueSpendByAccountFiltersPage(buyer)

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useRevenueSpendByAccountSortPage(buyer)

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useRevenueSpendByAccountList(setup, send)

	const [viewRevenueCpaDetailsInfo, setViewRevenueCpaDetailsInfo] = useState<TRevenueSpendByAccount | TRevenueSpendByAccountRevenueCpa | null>(null)
	const [viewRevenueSpendDetailsInfo, setViewRevenueSpendDetailsInfo] = useState<TRevenueSpendByAccount | TRevenueSpendByAccountRevenueSpend | null>(null)
	const [viewOfferSpendDetailsInfo, setViewOfferSpendDetailsInfo] = useState<TRevenueSpendByAccount | TRevenueSpendByAccountOfferSpend | null>(null)
	const [viewRevenueDetailsInfo, setViewRevenueDetailsInfo] = useState<TRevenueSpendByAccount | TRevenueSpendByAccountRevenue | null>(null)
	const [viewSpendDetailsInfo, setViewSpendDetailsInfo] = useState<TRevenueSpendByAccount | TRevenueSpendByAccountSpend | null>(null)
	const [showEmptySpendAgencyColumns, setShowEmptySpendAgencyColumns] = useState<boolean>(false)

	const onViewRevenueCpaDetailsInfo = (item: TRevenueSpendByAccount | TRevenueSpendByAccountRevenueCpa, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueCpaDetailsInfo(item)
	}

	const onViewRevenueSpendDetailsInfo = (item: TRevenueSpendByAccount | TRevenueSpendByAccountRevenueSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueSpendDetailsInfo(item)
	}

	const onViewOfferSpendDetailsInfo = (item: TRevenueSpendByAccount | TRevenueSpendByAccountOfferSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewOfferSpendDetailsInfo(item)
	}

	const onViewRevenueDetailsInfo = (item: TRevenueSpendByAccount | TRevenueSpendByAccountRevenue, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueDetailsInfo(item)
	}

	const onViewSpendDetailsInfo = (item: TRevenueSpendByAccount | TRevenueSpendByAccountSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewSpendDetailsInfo(item)
	}

	const onShowEmptySpendAgencyColumns = (e: React.MouseEvent) => {
		e.preventDefault()
		setShowEmptySpendAgencyColumns(prevState => !prevState)
	}

	return (
		<>
			<PageTitle title={
				<>
					<span>{pageTitleFormat}</span>
					{!!currentBuyerIsFired ? getAccountIsFiredBadge(currentBuyerIsFired, t) : null}
				</>
			}/>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>

						<button
							type="button"
							className="btn btn-primary d-flex align-items-center"
							onClick={onShowEmptySpendAgencyColumns}
						>
							{!showEmptySpendAgencyColumns ? <IconView/> : <IconViewOff/>}
							<span
								className="d-none d-lg-block">{
								!showEmptySpendAgencyColumns ?
									`${t(ALL_LANG_KEY.revenue_spend_by_account_page.btn_show_empty_spend_agency_columns)}` :
									`${t(ALL_LANG_KEY.revenue_spend_by_account_page.btn_hide_empty_spend_agency_columns)}`
							}</span>
						</button>
					</div>

					<Filters
						config={revenueSpendByAccountFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.RolePage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<RevenueSpendByAccountListTable
						items={items}
						skip={skip}
						sortBy={sortBy}
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onViewRevenueCpaDetails={onViewRevenueCpaDetailsInfo}
						onViewRevenueSpendDetails={onViewRevenueSpendDetailsInfo}
						onViewOfferSpendDetails={onViewOfferSpendDetailsInfo}
						onViewRevenueDetails={onViewRevenueDetailsInfo}
						onViewSpendDetails={onViewSpendDetailsInfo}
						showEmptySpendAgencyColumns={showEmptySpendAgencyColumns}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!viewRevenueCpaDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByAccountList) &&
				<RevenueSpendByAccountRevenueDetailsModal
					offerType={EOfferType.cpa}
					revenueSpend={viewRevenueCpaDetailsInfo}
					show={!!viewRevenueCpaDetailsInfo}
					take={take}
					onClose={() => {
						setViewRevenueCpaDetailsInfo(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!viewRevenueSpendDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByAccountList) &&
				<RevenueSpendByAccountRevenueDetailsModal
					offerType={EOfferType.spend}
					revenueSpend={viewRevenueSpendDetailsInfo}
					show={!!viewRevenueSpendDetailsInfo}
					take={take}
					onClose={() => {
						setViewRevenueSpendDetailsInfo(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!viewOfferSpendDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByAccountList) &&
				<RevenueSpendByAccountOfferSpendDetailsModal
					revenueSpend={viewOfferSpendDetailsInfo}
					show={!!viewOfferSpendDetailsInfo}
					take={take}
					onClose={() => {
						setViewOfferSpendDetailsInfo(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!viewRevenueDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByAccountList) &&
				<RevenueSpendByAccountRevenueDetailsModal
					revenueSpend={viewRevenueDetailsInfo}
					show={!!viewRevenueDetailsInfo}
					take={take}
					onClose={() => {
						setViewRevenueDetailsInfo(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!viewSpendDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByAccountList) &&
				<RevenueSpendByAccountSpendDetailsModal
					revenueSpend={viewSpendDetailsInfo}
					show={!!viewSpendDetailsInfo}
					take={take}
					onClose={() => {
						setViewSpendDetailsInfo(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}
		</>
	)
}
